import { useMutation, useQueryClient } from '@tanstack/react-query';
import BadgeService from '@clickersphere/core/services/BadgeService';
import { LEADS_QUERY_KEY, LeadQueryData } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { BADGES_QUERY_KEY, BadgeQueryData } from './useBadges';

interface IDetachBadgeVars {
  leadId: string;
  badgeId: string;
}

export function useDetachBadge() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: detachBadgeIsPending, mutateAsync: detachBadgeAsync } =
    useMutation({
      mutationFn: async ({ leadId, badgeId }: IDetachBadgeVars) =>
        BadgeService.detachFromLead(leadId, badgeId),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        const oldBadges = queryClient.getQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
        );
        const oldLeads = queryClient.getQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
        );
        const oldLead = queryClient.getQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        );

        const cachedLead =
          (oldLead?.id === variables.leadId ? oldLead : undefined) ??
          oldLeads?.find((l) => l.id === variables.leadId);
        const wasAttached =
          cachedLead?.badges?.some((b) => b.id === variables.badgeId) ?? false;

        if (wasAttached) {
          queryClient.setQueryData<BadgeQueryData[]>(
            buildQueryKey(BADGES_QUERY_KEY),
            (old) =>
              old?.map((badge) =>
                badge.id === variables.badgeId
                  ? { ...badge, usageCount: Math.max(badge.usageCount - 1, 0) }
                  : badge,
              ),
          );

          queryClient.setQueryData<LeadQueryData[]>(
            buildQueryKey(LEADS_QUERY_KEY),
            (old) =>
              old?.map((lead) =>
                lead.id === variables.leadId
                  ? {
                      ...lead,
                      badges:
                        lead.badges?.filter(
                          (b) => b.id !== variables.badgeId,
                        ) ?? [],
                    }
                  : lead,
              ),
          );

          queryClient.setQueryData<LeadQueryData>(
            buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
            (old) => {
              if (!old || old.id !== variables.leadId) return old;
              return {
                ...old,
                badges:
                  old.badges?.filter((b) => b.id !== variables.badgeId) ?? [],
              };
            },
          );
        }

        return { oldBadges, oldLeads, oldLead };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
      },
      onError: async (_error, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        queryClient.setQueryData(
          buildQueryKey(BADGES_QUERY_KEY),
          context?.oldBadges,
        );
        queryClient.setQueryData(
          buildQueryKey(LEADS_QUERY_KEY),
          context?.oldLeads,
        );
        queryClient.setQueryData(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
          context?.oldLead,
        );
      },
    });

  return {
    detachBadgeAsync,
    detachBadgeIsPending,
  };
}
